import {useEffect, useRef} from "react";
import {toast} from "sonner";

import {FILE_PICKER_GRACE_MS, isWithinFilePickerGracePeriod} from "@/src/modules/core/lib/file-picker-session";
import {isStandalonePwa} from "@/src/modules/core/lib/is-standalone-pwa";

import {useVersionChecker} from "./use-version-checker";

const AUTO_RELOAD_DELAY_MS = 1500;
const STANDALONE_AUTO_RELOAD_DELAY_MS = 400;

function canReloadNow() {
  return document.visibilityState === "visible" && !isWithinFilePickerGracePeriod();
}

export default function useNewVersion() {
  const {isNewVersionAvailable, reloadPage} = useVersionChecker();
  const pendingReloadRef = useRef(false);
  const timeoutRef = useRef<number | null>(null);

  useEffect(() => {
    const clearScheduledReload = () => {
      if (timeoutRef.current != null) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };

    const scheduleReload = () => {
      if (!canReloadNow()) {
        pendingReloadRef.current = true;
        if (isWithinFilePickerGracePeriod()) {
          clearScheduledReload();
          timeoutRef.current = window.setTimeout(
            () => {
              timeoutRef.current = null;
              scheduleReload();
            },
            FILE_PICKER_GRACE_MS + 250,
          );
        }
        return;
      }

      pendingReloadRef.current = false;
      clearScheduledReload();

      const reloadDelayMs = isStandalonePwa()
        ? STANDALONE_AUTO_RELOAD_DELAY_MS
        : AUTO_RELOAD_DELAY_MS;

      toast.info("A new version is available. Updating…", {duration: reloadDelayMs});
      timeoutRef.current = window.setTimeout(reloadPage, reloadDelayMs);
    };

    if (!isNewVersionAvailable) {
      pendingReloadRef.current = false;
      clearScheduledReload();
      return clearScheduledReload;
    }

    scheduleReload();

    const retryPendingReload = () => {
      if (!isNewVersionAvailable && !pendingReloadRef.current) {
        return;
      }
      scheduleReload();
    };

    document.addEventListener("visibilitychange", retryPendingReload);
    window.addEventListener("focus", retryPendingReload);

    return () => {
      clearScheduledReload();
      document.removeEventListener("visibilitychange", retryPendingReload);
      window.removeEventListener("focus", retryPendingReload);
    };
  }, [isNewVersionAvailable, reloadPage]);
}
